import { MenuItem as Mui4MenuItem, MenuItemProps as Mui4MenuItemProps } from '@material-ui/core'
import Checkbox from '@mui/material/Checkbox'
import Mui5MenuItem, { MenuItemProps as Mui5MenuItemProps } from '@mui/material/MenuItem'
import { styled } from '@mui/system'
import React, { forwardRef } from 'react'
import { useComponentVersion } from '../hooks'
import { OsMenuVersion } from './Menu'

// Define types
export type MenuItemContentType = 'menuItem' | 'separator' | 'sectionHeading'
export type MenuItemSelectionType = 'single' | 'multiple'
export type MenuItemColor = 'primary' | 'destructive'
export type MenuItemState = 'default' | 'hover' | 'focus'

export interface OsMenuItemProps extends Mui5MenuItemProps {
  contentType?: MenuItemContentType
  selectionType?: MenuItemSelectionType
  color?: MenuItemColor
  leadingIcon?: React.ReactNode
  description?: React.ReactNode | string
  trailingIcon?: React.ReactNode
  trailingText?: React.ReactNode | string
  onClick?: (event: React.MouseEvent) => void
  onCheckboxChange?: (event: React.MouseEvent) => void
  button?: Mui4MenuItemProps['button']
}

const StyledMenuItemTitle = styled('span')(
  ({ theme }: { theme: any }) => {
    return {
      ...theme.typography.body2Medium,
      display: 'flex',
      alignItems: 'center',
    }
  },
  { name: 'MenuItemTitle', slot: 'menuItemTitle' }
)

const StyledMenuItemDescription = styled('span')<{
  disabled?: boolean
  color?: MenuItemColor
}>(
  ({ theme, disabled, color }: { theme: any; disabled?: boolean; color?: MenuItemColor }) => {
    const textColor =
      disabled && color === 'destructive' ? theme.palette.neutralGrey[600] : theme.palette.neutralGrey[1000]

    return {
      ...theme.typography.body3,
      color: textColor,
    }
  },
  { name: 'MenuItemDescription', slot: 'menuItemDescription' }
)

const StyledSectionHeading = styled('span')(
  ({ theme }: { theme: any }) => {
    return {
      ...theme.typography.body3SemiBold,
      color: theme.palette.neutralGrey[900],
      cursor: 'default',
    }
  },
  { name: 'MenuItemDescription', slot: 'menuItemDescription' }
)

const StyledTrailingText = styled('span')(
  ({ theme }: { theme: any }) => {
    return {
      ...theme.typography.body4,
      color: theme.palette.neutralGrey[900],
    }
  },
  { name: 'TrailingText', slot: 'trailingText' }
)

const StyledMainTextWrapper = styled('div')({
  display: 'flex',
  flexFlow: 'column',
  gap: 4,
  paddingRight: 35,
})

const StyledTrailingContentWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginLeft: 'auto',
  '& .MuiSvgIcon-root': {
    height: 16,
    width: 16,
  },
  '& .OSUI-material-symbols': {
    fontSize: '16px !important',
  },
})

export const MenuItem = forwardRef<HTMLLIElement, OsMenuItemProps>((props, ref) => {
  const {
    contentType = 'menuItem',
    selectionType = 'single',
    color = 'primary',
    disabled = false,
    divider = false,
    selected = false,
    leadingIcon,
    children,
    description,
    trailingIcon,
    trailingText,
    onClick,
    onCheckboxChange,
    button,
    ...restProps
  } = props

  const version = useComponentVersion('menu')

  // Determine if we should show a leading icon or checkbox
  const showLeadingIcon = leadingIcon || selectionType === 'multiple'

  // Handle checkbox click - use onCheckboxChange if provided, otherwise onClick
  const handleCheckboxClick = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation() // Prevent menu from closing
    if (!disabled) {
      if (onCheckboxChange) {
        onCheckboxChange(event)
      } else if (onClick) {
        onClick(event as any)
      }
    }
  }

  // Handle MenuItem click - for multiple selection, handle here to prevent menu closing
  const handleMenuItemClick = (event: React.MouseEvent) => {
    // Prevent clicks on section headings
    if (contentType === 'sectionHeading') {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    if (selectionType === 'multiple') {
      event.preventDefault()
      event.stopPropagation()
      if (!disabled) {
        if (onCheckboxChange) {
          onCheckboxChange(event)
        } else if (onClick) {
          onClick(event)
        }
      }
    } else {
      // Single selection - let normal behavior occur
      if (onClick && !disabled) {
        onClick(event)
      }
    }
  }

  const leadingIconContent =
    selectionType === 'multiple' ? (
      <Checkbox
        checked={selected}
        disabled={disabled}
        size="small"
        tabIndex={-1} // Let the MenuItem handle focus
        // Don't add onClick to checkbox for multiple selection
      />
    ) : (
      leadingIcon
    )

  const showTrailingContent = trailingIcon || trailingText

  switch (version) {
    case OsMenuVersion.V2:
      return (
        <Mui5MenuItem
          ref={ref}
          disabled={disabled}
          divider={divider}
          selected={selected}
          onClick={handleMenuItemClick}
          data-color={color}
          data-selection-type={selectionType}
          data-content-type={contentType}
          {...restProps}
        >
          {contentType === 'menuItem' && (
            <>
              {showLeadingIcon && <> {leadingIconContent}</>}
              <StyledMainTextWrapper>
                <StyledMenuItemTitle>{children}</StyledMenuItemTitle>
                {description && (
                  <StyledMenuItemDescription disabled={disabled} color={color}>
                    {description}
                  </StyledMenuItemDescription>
                )}
              </StyledMainTextWrapper>
              {showTrailingContent && (
                <StyledTrailingContentWrapper>
                  {trailingText && <StyledTrailingText>{trailingText}</StyledTrailingText>}
                  {trailingIcon && <>{trailingIcon}</>}
                </StyledTrailingContentWrapper>
              )}
            </>
          )}
          {contentType === 'sectionHeading' && <StyledSectionHeading>{children}</StyledSectionHeading>}
        </Mui5MenuItem>
      )

    default:
      return <Mui4MenuItem ref={ref} {...(props as Mui4MenuItemProps)} button={true} />
  }
})
